import { runBulkDeleteWorkflow, runBulkMoveWorkflow } from "@/action-dialog";
import { useDeleteCanvas } from "@/api/canvas";
import { useDeleteFileRecord } from "@/api/fileRecords";
import { useDeleteFlowSession } from "@/api/flow_sessions";
import {
  useMultiSelectionObjectActionHandlers,
  useObjectActions,
} from "@/object-actions";
import type { Canvas, FileRecord, InboxItem } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useMemo } from "react";
import { WorkspaceSelectionBar } from "../../components/WorkspaceSelectionBar";
import { useWorkspaceSelection } from "../../hooks/useWorkspaceSelection";
import type { UnifiedItem } from "../../types";
import { cascadeDeleteFolder } from "../../utils/folderDelete";
import {
  browserItemSelectionId,
  type BrowserItem,
} from "../utils/homeBrowserTypes";

export interface UseBrowserSelectionBarParams {
  /** Every item the selection can resolve against (unfiltered by search, so
   *  bulk actions stay available when a search hides selected items). */
  items: BrowserItem[];
  /** Scoped workspace. Bulk move requires it; undefined (home) disables move. */
  workspaceId?: string;
  /** Full file-record set for folder cascade-delete and the move dialog. */
  allRecords: FileRecord[];
  canvasActions: {
    open: (canvas: Canvas) => void;
    rename: (canvas: Canvas) => void;
    share: (canvas: Canvas) => void;
  };
  fileActions: {
    openFile: (record: FileRecord) => void;
    rename: (record: FileRecord) => void;
    copy: (record: FileRecord) => void;
  };
  openChat: (item: InboxItem) => void;
  /** Folder-row/card open (workspace browser); folders open as files without it. */
  onFolderOpen?: (record: FileRecord) => void;
}

/**
 * Bulk-selection bar + single-selection keyboard open for the browser
 * surfaces. Resolves the workspace-selection ids back to items, derives the
 * capability set, and wires the registry's selection-bar actions.
 */
export function useBrowserSelectionBar({
  items,
  workspaceId,
  allRecords,
  canvasActions,
  fileActions,
  openChat,
  onFolderOpen,
}: UseBrowserSelectionBarParams) {
  const selection = useWorkspaceSelection();
  const queryClient = useQueryClient();

  const selectedItems = useMemo<UnifiedItem[]>(() => {
    if (selection.selectionCount === 0) return [];
    const byId = new Map<string, UnifiedItem>();
    for (const item of items) {
      const id = browserItemSelectionId(item);
      if (!id) continue;
      if (item.kind === "canvas")
        byId.set(id, { kind: "canvas", data: item.data });
      else if (item.kind === "chat")
        byId.set(id, { kind: "chat", data: item.data });
      else if (item.kind === "file") {
        byId.set(id, {
          kind: item.data.path.endsWith("/") ? "folder" : "file",
          data: item.data,
        });
      }
    }
    const result: UnifiedItem[] = [];
    for (const id of selection.selectedIds) {
      const item = byId.get(id);
      if (item) result.push(item);
    }
    return result;
  }, [selection.selectedIds, selection.selectionCount, items]);

  const capabilities = useMemo(() => {
    const count = selectedItems.length;
    const single = count === 1 ? selectedItems[0] : null;
    const hasChats = selectedItems.some((i) => i.kind === "chat");
    const canDuplicateSingle = count === 1 && single?.kind === "file";
    const canDuplicateMulti =
      count > 1 && selectedItems.every((i) => i.kind === "file");
    return {
      canOpen: count === 1,
      canRename:
        count === 1 && single?.kind !== "canvas" && single?.kind !== "chat",
      // Bulk move needs a workspace scope; chats can't be moved (yet).
      canMove: count > 0 && !hasChats && !!workspaceId,
      canDuplicate: canDuplicateSingle || canDuplicateMulti,
      canShare: count === 1 && single?.kind === "canvas",
      canDelete: count > 0,
    };
  }, [selectedItems, workspaceId]);

  const openUnifiedItem = useCallback(
    (item: UnifiedItem) => {
      if (item.kind === "canvas") canvasActions.open(item.data);
      else if (item.kind === "chat") openChat(item.data);
      else if (item.kind === "folder" && onFolderOpen) onFolderOpen(item.data);
      else fileActions.openFile(item.data as FileRecord);
    },
    [canvasActions, fileActions, openChat, onFolderOpen],
  );

  const handleSelectionOpen = useCallback(() => {
    if (selectedItems.length !== 1) return;
    openUnifiedItem(selectedItems[0]);
  }, [selectedItems, openUnifiedItem]);

  /** Enter-key open for `useSelectionKeyboard` — resolves a selection id. */
  const openSelectedId = useCallback(
    (id: string) => {
      const item = selectedItems.find(
        (i) => browserItemSelectionId(toBrowserItem(i)) === id,
      );
      if (item) openUnifiedItem(item);
    },
    [selectedItems, openUnifiedItem],
  );

  const handleSelectionRename = useCallback(() => {
    if (selectedItems.length !== 1) return;
    const item = selectedItems[0];
    if (item.kind === "canvas") canvasActions.rename(item.data);
    else if (item.kind !== "chat") fileActions.rename(item.data);
  }, [selectedItems, canvasActions, fileActions]);

  const handleSelectionDuplicate = useCallback(() => {
    for (const item of selectedItems) {
      if (item.kind === "file") fileActions.copy(item.data);
    }
  }, [selectedItems, fileActions]);

  const handleSelectionShare = useCallback(() => {
    if (selectedItems.length !== 1) return;
    const item = selectedItems[0];
    if (item.kind === "canvas") canvasActions.share(item.data);
  }, [selectedItems, canvasActions]);

  // Bulk deletes. Per-item dispatch; the shared dialog confirms once.
  const deleteChatSession = useDeleteFlowSession();
  const handleBulkDeleteChat = useCallback(
    async (chat: InboxItem) => {
      await deleteChatSession.mutateAsync(chat.flow_session_id);
      queryClient.invalidateQueries({ queryKey: ["workspace-flow-sessions"] });
    },
    [deleteChatSession, queryClient],
  );
  const deleteCanvas = useDeleteCanvas();
  const handleBulkDeleteCanvas = useCallback(
    async (canvas: Canvas) => {
      await deleteCanvas.mutateAsync(canvas.id);
    },
    [deleteCanvas],
  );
  const deleteFileRecord = useDeleteFileRecord(workspaceId);
  const handleBulkDeleteFileRecord = useCallback(
    async (record: FileRecord) => {
      // Folders cascade — the API only deletes the marker row, so children
      // (selected or not) are removed explicitly first.
      if (record.path.endsWith("/")) {
        await cascadeDeleteFolder(
          record,
          allRecords,
          queryClient,
          workspaceId ?? "",
        );
        return;
      }
      await deleteFileRecord.mutateAsync(record.id);
    },
    [deleteFileRecord, allRecords, queryClient, workspaceId],
  );

  const openBulkMove = useCallback(() => {
    if (!workspaceId) return;
    void runBulkMoveWorkflow({
      workspaceId,
      allRecords,
      items: selectedItems,
      onMoved: () => selection.clear(),
    });
  }, [workspaceId, allRecords, selectedItems, selection]);

  const openBulkDelete = useCallback(() => {
    void runBulkDeleteWorkflow({
      items: selectedItems,
      onDeleteCanvas: handleBulkDeleteCanvas,
      onDeleteFileRecord: handleBulkDeleteFileRecord,
      onDeleteChat: handleBulkDeleteChat,
      onDeleted: () => selection.clear(),
    });
  }, [
    selectedItems,
    handleBulkDeleteCanvas,
    handleBulkDeleteFileRecord,
    handleBulkDeleteChat,
    selection,
  ]);

  const selectionKinds = useMemo(() => {
    const kinds = new Set<"canvas" | "chat" | "file" | "folder">();
    for (const item of selectedItems) kinds.add(item.kind);
    return Array.from(kinds);
  }, [selectedItems]);

  const multiSelectionHandlers = useMultiSelectionObjectActionHandlers(
    useMemo(
      () => ({
        onOpen: handleSelectionOpen,
        onRename: handleSelectionRename,
        onBulkMove: openBulkMove,
        onDuplicate: handleSelectionDuplicate,
        onShare: handleSelectionShare,
        onBulkDelete: openBulkDelete,
        canOpen: capabilities.canOpen,
        canRename: capabilities.canRename,
        canMove: capabilities.canMove,
        canDuplicate: capabilities.canDuplicate,
        canShare: capabilities.canShare,
        canDelete: capabilities.canDelete,
      }),
      [
        handleSelectionOpen,
        handleSelectionRename,
        openBulkMove,
        handleSelectionDuplicate,
        handleSelectionShare,
        openBulkDelete,
        capabilities,
      ],
    ),
  );

  const { actions: selectionBarActions } = useObjectActions({
    kind: { type: "multi-selection", kinds: selectionKinds },
    surface: "selection-bar",
    enabled: selection.selectionCount > 1,
    state: {
      selectionSingle: capabilities.canOpen,
      canRenameSelection: capabilities.canRename,
      canDuplicateSelection: capabilities.canDuplicate,
      canShare: capabilities.canShare,
      hasChatsInSelection: selectedItems.some((i) => i.kind === "chat"),
    },
    handlers: multiSelectionHandlers,
  });

  const selectionBar: React.ReactNode =
    selection.selectionCount > 1 ? (
      <WorkspaceSelectionBar
        count={selection.selectionCount}
        registryActions={selectionBarActions}
        onClose={selection.clear}
      />
    ) : null;

  return { selectionBar, openSelectedId };
}

function toBrowserItem(item: UnifiedItem): BrowserItem {
  if (item.kind === "canvas") return { kind: "canvas", data: item.data };
  if (item.kind === "chat") return { kind: "chat", data: item.data };
  return { kind: "file", data: item.data };
}
